'use client'
import {
  useIsGuestUser,
  useIsPickup,
  useUserDefaultShoppingAddressId,
  useUserId,
  useZipForZipOnlySignedInUser,
} from '@/features/account/services/User/hooks'
import styled from 'styled-components'
import { useEffect } from 'react'
import { cleanUrlOfParams } from '@/features/shared/utils/url'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { CMS_GHP_SLUG, useCMSParams, useQueryCMS } from '@/features/cms/services/CMS/queries'
import { getRedemptionModalContent } from '@/features/account/components/Modal/MembershipSuccessModal/utils'
import { ErrorPage, ErrorPageBase } from '@/features/shared/components/ErrorPage'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { GlobalHomeModals } from '@/features/shop/components/GlobalHomePage/GlobalHomeModals'
import { DynamicGlobalAddressModal } from '@/features/account/components/Modal/GlobalAddressModal/DynamicGlobalAddressModal'
import { DynamicConfirmationModal } from '@/features/shared/components/Modal/ConfirmationModal/DynamicConfirmationModal'
import { DynamicGuestAddressModal } from '@/features/account/components/Modal/GuestAddressModal/DynamicGuestAddressModal'
import { DynamicMembershipSuccessModal } from '@/features/account/components/Modal/MembershipSuccessModal/DynamicMembershipSuccessModal'
import { DynamicWelcomeModal } from '@/features/entrypoint/components/Modal/WelcomeModal/DynamicWelcomeModal'
import { screenSizes } from '@shipt/design-system-themes'
import { Box, Row } from '@shipt/design-system-layouts'
import { type InferGetServerSidePropsType } from 'next'
import { type getServerSideProps } from '@/pages/shop'
import { LoadingIndicator } from '@shipt/design-system-loading'
import { CMSLayoutContextProvider } from '@/features/cms/components/CMS/CMSLayoutContext'
import { CMSSection } from '@/features/cms/components/CMS/CMSSection'
import { useIsMutating } from '@tanstack/react-query'
import { SelectStoreMutationKey } from '@/features/shop/services/ShoppingStore/mutations'
import { useQueryShoppingStores } from '@/features/shop/services/ShoppingStore/queries'
import { getErrorStatus } from '@/features/shared/utils/dataFetching/utils'
import { SEOWrapper } from '@/features/shop/components/GlobalHomePage/SEOWrapper'
import { useQueryMembershipEntryPointBanner } from '@/features/shop/services/Banners/queries'
import { useShouldFetchMembershipEntryPointBanner } from '@/features/account/components/MembershipEntryPointBanner/hooks'
import { useLazyInitializedRef } from '@/features/shared/hooks/useLazyInitializedRef'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { GlobalHomePageLayout } from '@/features/cms/components/CMS/components/Layouts/GlobalHomePageLayout'
import { Button } from '@shipt/design-system-buttons'

export type GHPProps = InferGetServerSidePropsType<typeof getServerSideProps>

export const GlobalHomePage = ({ c360MembershipId, checkC360Membership }: GHPProps) => {
  return (
    <>
      <GlobalHomeModals
        c360MembershipId={c360MembershipId}
        checkC360Membership={checkC360Membership}
      />
      <GlobalHomePageContent suppressCmsModal={!!checkC360Membership} />
    </>
  )
}

const GlobalHomePageContent = ({ suppressCmsModal }: { suppressCmsModal: boolean }) => {
  const { openDialog } = useHandleDialog()
  // prefetch membershipentrypoint banner to prevent layout shift
  const shouldFetchMembershipBanner = useShouldFetchMembershipEntryPointBanner()
  useQueryMembershipEntryPointBanner(shouldFetchMembershipBanner, true)

  const { prevStore, success } = useSearchQueryParams()
  const isGuestUser = useIsGuestUser()
  const userId = useUserId()
  const userDefaultShoppingAddressId = useUserDefaultShoppingAddressId()
  const C360ProvidedZip = useZipForZipOnlySignedInUser()
  const { error } = useQueryShoppingStores()

  const isSelectingStore = useIsMutating({ mutationKey: [SelectStoreMutationKey] }) > 0

  const cmsParams = useCMSParams(CMS_GHP_SLUG)
  const {
    data: cmsData,
    isPending: isPendingCMS,
    error: cmsError,
  } = useQueryCMS({
    params: cmsParams,
    keepPreviousData: true,
    enabled: Boolean(!isSelectingStore && (cmsParams.addressParams?.zip || C360ProvidedZip)),
    enableDeferredHydration: true,
    trackPageContentVersion: true,
  })

  const typeText = useIsPickup() ? 'pickup' : 'delivery'

  useEffect(() => {
    if (success && typeof success === 'string' && userDefaultShoppingAddressId) {
      const { title, body } = getRedemptionModalContent(success)
      openDialog(DynamicMembershipSuccessModal, { title, content: body })
      cleanUrlOfParams('success')
    }
  }, [openDialog, success, userDefaultShoppingAddressId])

  useEffect(() => {
    if (typeof prevStore === 'string') {
      const storeName = prevStore.trim()
      const modalTitle = storeName
        ? `Unfortunately, ${storeName} is unavailable near this address.`
        : 'The store you are attempting to visit is unavailable.'
      const modalMessage = storeName
        ? `With the address you entered you won't be able to place an order on ${storeName}. To continue shopping, choose a different store or change the ${typeText} address.`
        : `To continue shopping, choose a different store or change the ${typeText} address.`
      const acceptButtonLabel = 'Select a new store'
      const rejectButtonLabel = 'Change address'
      const properties: Partial<Parameters<typeof trackElementViewed>>[0] = {
        type: 'modal',
        display_sublocation: 'unavailable_store_confirmation_modal',
      }
      trackElementViewed({ ...properties, message_goal: modalTitle })

      const handleConfirm = () => {
        trackElementClicked({ ...properties, message_goal: acceptButtonLabel })
      }
      const handleReject = () => {
        trackElementClicked({ ...properties, message_goal: rejectButtonLabel })
        isGuestUser
          ? openDialog(DynamicGuestAddressModal, { startChangeStep: true })
          : openDialog(DynamicGlobalAddressModal)
      }
      openDialog(DynamicConfirmationModal, {
        size: 'md',
        contentLabel: 'Select a new store',
        modalTitle,
        modalMessage,
        closeOnReject: false,
        handleConfirm,
        handleReject,
        acceptButtonLabel,
        rejectButtonLabel,
      })
      cleanUrlOfParams('prevStore')
    }
  }, [typeText, prevStore, openDialog, isGuestUser])

  const isInitiallyGuestRef = useLazyInitializedRef(() => isGuestUser)

  // TODO: Remove this useEffect once we've fully migrated over to Auth0 - https://app.clickup.com/t/8677w56vu
  useEffect(() => {
    if (
      userId &&
      !userDefaultShoppingAddressId &&
      !isInitiallyGuestRef.current &&
      !C360ProvidedZip
    ) {
      openDialog(DynamicWelcomeModal)
    }
  }, [openDialog, isInitiallyGuestRef, userId, userDefaultShoppingAddressId, C360ProvidedZip])

  const cmsHeaderSectionIsShowing =
    cmsData?.content.content_type_id === 'global_home_page_layout' &&
    !!cmsData.content.data?.header?.length

  if (error) {
    const errorStatus = getErrorStatus(error)
    if (errorStatus === 404 && Boolean(C360ProvidedZip)) {
      return (
        <SEOWrapper>
          <ErrorPageBase
            errorTitle="This zip code is not covered by Shipt yet."
            errorMessage="Please enter a different address"
          >
            <Button onClick={() => openDialog(DynamicGlobalAddressModal)}>Add address</Button>
          </ErrorPageBase>
        </SEOWrapper>
      )
    }
  } else if (cmsError) {
    return (
      <SEOWrapper>
        <ErrorPage />
      </SEOWrapper>
    )
  }
  // loader skeletons for cms shelves are handled at the shelf level
  if (isPendingCMS)
    return (
      <SEOWrapper>
        <Row align="center" justify="center" paddingTop="5xl">
          <LoadingIndicator size="xl" />
        </Row>
      </SEOWrapper>
    )

  return (
    <SEOWrapper cmsContent={cmsData?.content}>
      <HomepageContent
        marginBottom="xxl"
        paddingTop={
          // if rendering the cms header, we do not want to apply a top margin.
          // if not, we do want to apply top margin
          cmsHeaderSectionIsShowing ? 'unset' : { base: 'lg', sm: 'unset' }
        }
      >
        {cmsData?.content.content_type_id === 'global_home_page_layout' &&
          !!cmsData.content.data?.header?.length && (
            <Box marginBottom={{ base: 'sm', md: 'xl' }}>
              <CMSSection
                section={cmsData.content.data.header}
                // carousels can persist consistent data in the UI tree on shelf driven pages
                // causing only children to re-render instead of entire carousel
                key={cmsParams.addressParams?.address_line_1}
              />
            </Box>
          )}
        {cmsData?.content.content_type_id === 'global_home_page_layout' && (
          <CMSLayoutContextProvider data={cmsData.content.content_type_id}>
            <GlobalHomePageLayout
              layoutData={cmsData.content}
              // carousels can persist consistent data in the UI tree on shelf driven pages
              // causing only children to re-render instead of entire carousel
              key={cmsParams.addressParams?.address_line_1}
              suppressCmsModal={suppressCmsModal}
            />
          </CMSLayoutContextProvider>
        )}
      </HomepageContent>
    </SEOWrapper>
  )
}

const HomepageContent = styled(Box)`
  @media ${screenSizes.max_mobile} {
    overflow-x: hidden;
  }
`
